import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Instagram } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="site-layout">
      <Header open={open} setOpen={setOpen} />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function LogoMark() {
  // Placeholder circular logo slot — the actual logo file will be dropped in here.
  return (
    <Link
      to="/"
      data-testid="nav-logo-link"
      className="flex items-center gap-3 group"
      aria-label="The Life Refinery — home"
    >
      <span
        data-testid="nav-logo-mark"
        className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border transition-all"
        style={{
          borderColor: "#C9A84C",
          background: "#1B4332",
          color: "#FAF6EC",
        }}
      >
        <span className="font-serif text-lg leading-none" style={{ letterSpacing: "0.02em" }}>
          LR
        </span>
      </span>
      <span className="hidden sm:flex flex-col leading-tight">
        <span className="font-serif text-lg" style={{ color: "#1B4332" }}>
          The Life Refinery
        </span>
        <span className="font-script text-xl -mt-1" style={{ color: "#C9A84C" }}>
          Where You&rsquo;re Heard
        </span>
      </span>
    </Link>
  );
}

function Header({ open, setOpen }) {
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-md"
      style={{ background: "rgba(250,246,236,0.85)", borderBottom: "1px solid rgba(27,67,50,0.08)" }}
    >
      <div className="container-refined flex items-center justify-between py-4">
        <LogoMark />

        <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-[#1B4332] font-medium" : "text-[#2A2621]/70 hover:text-[#1B4332]"
                }`
              }
            >
              <span className="relative">
                {n.label}
              </span>
            </NavLink>
          ))}
          <Link to="/contact" data-testid="nav-cta-book" className="btn-gold">
            Book a discovery call
          </Link>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border"
          style={{ borderColor: "#1B4332", color: "#1B4332" }}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden border-t"
          style={{ borderColor: "rgba(27,67,50,0.08)", background: "#FAF6EC" }}
        >
          <div className="container-refined py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-serif text-2xl ${isActive ? "text-[#1B4332]" : "text-[#2A2621]/70"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="nav-mobile-cta" className="btn-gold mt-2 self-start">
              Book a discovery call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="mt-24"
      style={{ background: "#1B4332", color: "#FAF6EC" }}
    >
      <div className="container-refined py-16 grid gap-10 md:grid-cols-3">
        <div>
          <div className="font-serif text-3xl" style={{ color: "#FAF6EC" }}>The Life Refinery</div>
          <div className="font-script text-3xl mt-1" style={{ color: "#C9A84C" }}>Where You&rsquo;re Heard</div>
          <p className="mt-5 text-sm leading-relaxed text-[#FAF6EC]/75 max-w-sm">
            Coaching for working men moving from blocked and exhausted to clear, confident, and
            purposeful.
          </p>
        </div>

        <div>
          <div className="eyebrow" style={{ color: "#C9A84C" }}>Explore</div>
          <ul className="mt-4 space-y-2 text-sm text-[#FAF6EC]/80">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} data-testid={`footer-link-${n.label.toLowerCase()}`} className="hover:text-[#C9A84C] transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="eyebrow" style={{ color: "#C9A84C" }}>Elsewhere</div>
          <a
            href="https://instagram.com/refinewithpriya"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="footer-instagram"
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#FAF6EC]/85 hover:text-[#C9A84C] transition-colors"
          >
            <Instagram size={16} /> @refinewithpriya
          </a>
          <p className="mt-6 text-xs text-[#FAF6EC]/50">
            &copy; {new Date().getFullYear()} The Life Refinery. Founded by Priya Kanagaraj.
          </p>
        </div>
      </div>
    </footer>
  );
}
